import { postAnalyticsTrackClick } from "@/api/generated/BMWApi"
import { BaseCarModel, CarModel } from "@/api/generated/BMWInterfaces"
import LinkWithParams from "@/lib/common/LinkWithParams"
import colors from "@/lib/theme/colors"
import { formatter, getCardTimeLeftHours, getStockText } from "@/lib/utils"
import styled from "@emotion/styled"
import { Box, Button, Typography } from "@mui/material"
import Image from "next/image"
import { useEffect, useState } from "react"

const CarCard = styled(Button)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-self: stretch;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #f2f2f2;
  background: white;
  height: auto;
  min-height: 343px;
  padding: 0;
  position: relative;
  :hover {
    background-color: transparent;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    height: 100%;
  }
`

export const NewCarLabel = styled(Typography)`
  display: inline-flex;
  padding: 1px 5px 0 5px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  gap: -4px;
  border-radius: 4px;
  border: 1px solid var(--Direct-Azure, #61dfff);
  background: var(--Direct-White, #fff);
  color: var(--Direct-Black, #231f20);
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`

const StockContainer = styled.div`
  display: flex;
  padding: 0px 8px;
  align-items: center;
  gap: 4px;
  backdrop-filter: blur(2px);
  position: absolute;
  top: 0;
  right: 0;
  border-radius: 0 8px;
  background: var(--Direct-Black, #231f20);
`

const ImageContainer = styled.div`
  width: 100%;
  height: 146px;
  position: relative;
  flex-shrink: 0;
  display: flex;
  position: relative;
`

const ModelDetails = styled.div`
  display: flex;
  padding: 0px 8px 8px 8px;
  flex-direction: column;
  justify-content: center;
  gap: 16px;
  align-self: stretch;
  flex-grow: 1;
`

const ModelInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  height: 100%;
`

const PriceActions = styled.div``

const PricesBox = styled.div`
  display: flex;
  gap: 4px;
  flex: 1 0 0;
  margin: 1px 0;
  align-items: center;
`

const TagsFlex = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  row-gap: 8px;
  align-self: stretch;
  flex-wrap: wrap;
  padding: 12px 0 8px;
  border-bottom: 1px solid var(--Direct-very-light-grey, #f2f2f2);
`

export const Tag = styled.div<{ withoutBullet?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;

  ${(p) =>
    !p.withoutBullet &&
    `&::before {
      content: "";
      display: inline-block;
      width: 6px;
      height: 6px;
      min-width: 6px;
      background-color: #dedede;
      border-radius: 50%;
    }`}
`

const LowerTags = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 8px;
`

export const LowerTag = styled.div`
  display: flex;
  width: fit-content;
  gap: 4px;
`

const BottomContent = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledButton = styled(Button)`
  padding: 4px 8px 4px 2px;
  background-color: ${colors.directYellow};
`

const BenefitBanner = styled.div`
  display: flex;
  padding: 0px 8px;
  align-items: center;
  gap: 4px;
  backdrop-filter: blur(2px);
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  border-radius: 8px 0;
  background: ${colors.directAzure};
`

const SoldoutLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #fff;
  opacity: 0.7;
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: center;
`

const SoldOutSpan = styled(Typography)`
  display: flex;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 8px;
  background: #231f20;
  backdrop-filter: blur(2px);
  color: white;
  opacity: 1;
`

const SkeletonPulse = styled.div`
  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
`

const SkeletonBox = styled(SkeletonPulse)`
  background-color: #eaeaea;
`

const SkeletonImage = styled(SkeletonBox)`
  width: 100%;
  height: 146px;
  border-radius: 8px 8px 0px 0px;
  ${(props) => props.theme.breakpoints.down("md")} {
    width: 328px;
  }
`

const SkeletonLine = styled(SkeletonBox)<{
  width?: string
  height?: string
  borderRadius?: string
}>`
  width: ${(props) => props.width || "100%"};
  height: ${(props) => props.height || "16px"};
  border-radius: ${(props) => props.borderRadius || "0px"};
`

const DiscountedPrice = styled.span`
  display: inline-block;
  position: relative;
  img {
    position: absolute;
    top: 2px;
    left: 50%;
    transform: translateX(-50%);
  }
`

const Timer = styled(Typography)`
  display: flex;
  padding: 4px 8px;
  justify-content: flex-end;
  align-items: center;
  gap: 4px;
  border-radius: 6px;
  background: rgba(196, 47, 47, 0.08);
  backdrop-filter: blur(2px);
  color: ${colors.directLightRed};
  min-width: 67px;
`
const StyledSubTitle = styled(Typography)`
  font-size: 12px;
  ${(p) => p.theme.breakpoints.up("md")} {
    font-size: 14px;
  }
`

function CarsCardSkeleton(): React.ReactElement {
  return (
    <CarCard disableRipple disabled>
      <ImageContainer>
        <SkeletonImage />
      </ImageContainer>
      <Box display="flex" flexDirection="column" gap="8px" width={"100%"} padding="8px">
        <Box
          borderBottom="1px solid #F2F2F2"
          pb={1}
          display="flex"
          flexDirection="column"
          gap="8px"
        >
          <SkeletonLine width="166px" height="20px" />

          <SkeletonLine width="110px" height="20px" />
          <Box mt={0.5}>
            <SkeletonLine width="100%" height="20px" />
          </Box>
        </Box>
        <SkeletonLine width="100%" height="29px" borderRadius="6px" />
        <Box display="flex" flexDirection="column" gap="16px">
          <SkeletonLine width="221px" height="79px" />
          <SkeletonLine width="232px" height="67px" />
          <SkeletonLine width="100%" height="32px" borderRadius="6px" />
        </Box>
      </Box>
    </CarCard>
  )
}

export default function CarsCard({
  data,
  onClick,
  h2,
  loading,
}: {
  data?: CarModel | BaseCarModel
  onClick?: () => void
  h2?: boolean
  loading?: boolean
}): React.ReactElement {
  const [timeLeft, setTimeLeft] = useState(data?.dueDate ? getCardTimeLeftHours(data.dueDate) : "")
  const [isMounted, setIsMounted] = useState(false)
  const stock = data?.stock
  const dueDate = data?.dueDate
  const soldOut = stock === 0
  const stockText = getStockText(stock ?? 0, data?.displayLastInStock ?? true)
  const tagsObject = data?.tags as unknown as {
    year: string
    interior: string
    hand: string
    engineType: string
    color: string
  }
  useEffect(() => {
    // Check if we should show timer even when sold out (if stripDate exists and is in the future)
    const hasValidDate = data?.dueDate && new Date(data?.dueDate) > new Date()

    if (soldOut && !hasValidDate) {
      return
    }
    if (!dueDate) {
      return
    }
    const timer = setInterval(() => {
      setTimeLeft(getCardTimeLeftHours(dueDate))
    }, 1000)

    return () => clearInterval(timer)
  }, [dueDate, soldOut, data?.dueDate])

  useEffect(() => {
    setIsMounted(true)
  }, [])

  const showStrip = data?.specialStrip?.duoDate
    ? new Date(data?.specialStrip.duoDate) >= new Date()
    : false

  const getTimerDisplay = (): string | null => {
    // If the car is sold out, we don't show the timer
    if (soldOut) {
      return null
    }
    return isMounted ? timeLeft : ""
  }

  const handleTrackClick = (): void => {
    if (!data?.id) {
      return
    }
    postAnalyticsTrackClick({
      carModelId: data.id,
      ipAddress: "",
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    })
  }
  if (loading || !data) {
    return <CarsCardSkeleton />
  }

  const fee = data.km === 0 ? 257 : 250
  return (
    <CarCard
      disableRipple
      LinkComponent={LinkWithParams}
      href={`/cars/${data.id}`}
      onClick={handleTrackClick}
    >
      <ImageContainer>
        {soldOut && (
          <SoldoutLayer>
            <SoldOutSpan fontSize={16} fontWeight={500}>
              SOLD OUT
            </SoldOutSpan>
          </SoldoutLayer>
        )}
        {data.mainImage?.imageUrl && (
          <Image
            src={data.mainImage.imageUrl}
            alt={data.mainImage.alt || data.name}
            fill
            style={{ objectFit: "contain" }}
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          />
        )}
      </ImageContainer>
      <ModelDetails>
        <ModelInfo>
          <Typography variant="body1" fontSize={20} component={h2 ? "h2" : "p"}>
            {data.name}
          </Typography>
          <Typography variant="body1" fontSize={14} component={"p"}>
            {data.finishingLevelName}
          </Typography>
          <TagsFlex>
            {data?.isNewCar ? (
              <>
                <NewCarLabel>רכב חדש</NewCarLabel>
                <Tag>
                  <Typography variant="body2" fontSize={14}>
                    {tagsObject.engineType}
                  </Typography>
                </Tag>
              </>
            ) : (
              <>
                {tagsObject?.hand && (
                  <Tag withoutBullet>
                    <Typography variant="body2" fontSize={14}>
                      {tagsObject.hand}
                    </Typography>
                  </Tag>
                )}
                {tagsObject?.year && (
                  <Tag>
                    <Typography variant="body2" fontSize={14}>
                      {tagsObject.year}
                    </Typography>
                  </Tag>
                )}
                {(data?.km || data?.km === 0) && (
                  <Tag>
                    <Typography variant="body2" fontSize={14}>
                      {data?.km} ק״מ
                    </Typography>
                  </Tag>
                )}
                {tagsObject?.engineType && (
                  <Tag>
                    <Typography variant="body2" fontSize={14}>
                      {tagsObject.engineType}
                    </Typography>
                  </Tag>
                )}
              </>
            )}
          </TagsFlex>
          <LowerTags>
            <Box display={"flex"} flexDirection={"column"} gap={"8px"}>
              {data?.specialOfferTag?.specialTag
                ?.filter((el) => el.tag)
                ?.map((el, i) => (
                  <LowerTag key={i}>
                    <Image
                      alt="year"
                      style={{ marginTop: "2.5px" }}
                      width={16}
                      height={16}
                      src="/assets/discount-icon.svg"
                    />
                    <Typography variant="body2" fontSize={14} lineHeight={"21px"}>
                      {el.tag}
                    </Typography>
                  </LowerTag>
                ))}
            </Box>
          </LowerTags>
        </ModelInfo>
        <BottomContent>
          {data.noFeatureStock ? (
            <StyledSubTitle
              variant="h2"
              fontWeight={"400"}
              lineHeight={"100%"}
              color={colors.directGray}
            >
              הצעה לרכב 0 ק״מ
            </StyledSubTitle>
          ) : data.discountPrice > 0 ? (
            <Typography color={colors.directGray} variant="body3" fontSize={14}>
              מחיר מחירון (רכב חדש){" "}
              <DiscountedPrice>
                <Image src={"/assets/line-through-icon.svg"} alt="" width={59} height={17} />
                {formatter.format(data.price)}
              </DiscountedPrice>
            </Typography>
          ) : null}
          <PriceActions>
            <PricesBox>
              {data.discountPrice > 0 ? (
                <Typography
                  variant="body2"
                  fontSize={24}
                  fontWeight={"700"}
                  lineHeight={"28px"}
                  color={getTimerDisplay() ? colors.directLightRed : colors.directBlack}
                >
                  {formatter.format(data.discountPrice)}
                </Typography>
              ) : (
                <Typography
                  variant="h3"
                  fontSize={24}
                  fontWeight={"700"}
                  lineHeight={"28px"}
                  color={getTimerDisplay() ? colors.directLightRed : colors.directBlack}
                >
                  {formatter.format(data.price)}
                </Typography>
              )}
              {getTimerDisplay() && (
                <Timer fontSize={14} fontWeight={"700"}>
                  {getTimerDisplay()}
                </Timer>
              )}
            </PricesBox>
            {data?.isNewCar ? (
              <Typography fontSize={12} color={colors.directGray}>
                *המחירים כוללים אגרת רישוי
              </Typography>
            ) : (
              <Typography fontSize={12} color={colors.directGray}>
                {`לא כולל אגרת שינוי רישום הבעלות בסך ${fee} ₪`}
              </Typography>
            )}
          </PriceActions>
        </BottomContent>
        <StyledButton
          size="small"
          variant="contained"
          color="secondary"
          role="presentation"
          tabIndex={-1}
          onClick={onClick}
          endIcon={<Image alt="" src={"/assets/chevronleft.svg"} width={16} height={16} />}
        >
          {soldOut ? "יצירת קשר" : "לפרטים ולהזמנה"}
        </StyledButton>
      </ModelDetails>
      {showStrip && data?.specialStrip?.title && (
        <BenefitBanner>
          <Image src={"/assets/benefit-stars.svg"} alt="" width={12} height={12} />
          <Typography variant="body2" fontWeight={400} lineHeight={"24px"}>
            {data.specialStrip.title}
          </Typography>
        </BenefitBanner>
      )}
      {stockText && (
        <StockContainer>
          <Typography
            color={"white"}
            variant="body2"
            fontWeight={500}
            fontSize={16}
            lineHeight={"24px"}
          >
            {/* {getTimerDisplay()} */}
            {stockText}
          </Typography>
        </StockContainer>
      )}
    </CarCard>
  )
}
